import { useEffect, useRef, useState } from "react";
import { ScanFace, CheckCircle2 } from "lucide-react";

export const FaceScan = ({ t, verified, onVerified }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [state, setState] = useState(verified ? "done" : "idle");

  useEffect(() => () => streamRef.current?.getTracks().forEach((tr) => tr.stop()), []);

  const startScan = async () => {
    setState("scanning");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (e) {
      // camera denied/unavailable: simulate scan
    }
    setTimeout(() => {
      streamRef.current?.getTracks().forEach((tr) => tr.stop());
      setState("done");
      onVerified();
    }, 4000);
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col items-center text-center" data-testid="face-scan-card">
      <h3 className="font-heading font-bold text-white mb-1">{t.faceScanTitle}</h3>
      <p className="text-xs text-[#A0A0B0] mb-5 max-w-xs">{t.faceScanInstr}</p>

      <div className="relative w-44 h-44 mb-5">
        <div className={`absolute inset-0 rounded-full border-2 ${state === "done" ? "border-[#00FFAA]" : "border-[#00F5FF]/60"} transition-colors`} />
        {state === "scanning" && (
          <>
            <div className="absolute inset-[-8px] rounded-full border border-[#00FFAA]/40 animate-ping" />
            <div className="absolute inset-2 rounded-full overflow-hidden">
              <div className="absolute left-0 right-0 h-0.5 bg-[#00FFAA] shadow-[0_0_12px_#00FFAA] animate-scan-line" />
            </div>
          </>
        )}
        <div className="absolute inset-2 rounded-full overflow-hidden bg-[#0F0F1A] flex items-center justify-center">
          {state === "scanning" ? (
            <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="face-scan-video" />
          ) : state === "done" ? (
            <CheckCircle2 size={64} className="text-[#00FFAA]" />
          ) : (
            <ScanFace size={64} className="text-[#00F5FF]/50" />
          )}
        </div>
        {["top-0 left-0 border-t-2 border-l-2", "top-0 right-0 border-t-2 border-r-2", "bottom-0 left-0 border-b-2 border-l-2", "bottom-0 right-0 border-b-2 border-r-2"].map((pos, i) => (
          <div key={i} className={`absolute w-6 h-6 ${pos} border-[#00FFAA] rounded-sm opacity-70`} />
        ))}
      </div>

      {state === "done" ? (
        <p className="text-[#00FFAA] text-sm font-bold flex items-center gap-2" data-testid="face-scan-confirmed">
          <CheckCircle2 size={16} /> {t.faceConfirmed}
        </p>
      ) : (
        <button
          type="button"
          data-testid="start-face-scan-btn"
          onClick={startScan}
          disabled={state === "scanning"}
          className="px-6 py-3 rounded-full text-xs font-bold uppercase tracking-widest bg-[#00F5FF]/10 border border-[#00F5FF]/40 text-[#00F5FF] hover:bg-[#00F5FF]/20 transition-colors disabled:opacity-60"
        >
          {state === "scanning" ? t.scanning : t.startFaceScan}
        </button>
      )}
    </div>
  );
};
